<template>
  <div class="nebCheckboxGroup">
    <div class="nebCheckboxGroup-section" v-for="section in sections"
      :key="section"
    >
      <div class="nebCheckboxGroup-sectionTitle">
        <h4>{{ section }}</h4>
      </div>

      <div class="nebCheckboxGroup-checkboxes">
        <div class="nebCheckboxGroup-checkboxes-checkbox"
          v-for="opt in optionsForSection(section)"
          :key="opt.value"
          :class="columns"
        >
          <neb-checkbox :value="includes(value, opt.value)"
            :label="opt.label"
            :disabled="opt.disabled || false"
            @input="() => onInput(opt)"
          ></neb-checkbox>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uniq, includes, isNull, isUndefined } from 'lodash'

import NebCheckbox from '../Checkbox'

export default {
  name: 'NebCheckboxGroup',

  components: {
    NebCheckbox
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    /**
     * {Object[]} [options=[]] - Checkbox options. Each option is a checkbox.
     *
     * {String} options[].label - The option/checkbox label.
     *
     * {String} options[].value - The option value.
     *
     * {Boolean} options[].disabled - Whether or not the option should be disabled.
     *
     * {String} options[].section - Optional key. Use this to divide the
     * options into sections.
     */
    options: {
      type: Array[Object],
      default: () => []
    },
    /**
     * How many columns of checkboxes there should be.
     *
     * - Options are 'one', 'two', 'three', 'four', 'five'
     */
    columns: {
      type: String,
      default: 'three'
    }
  },

  computed: {
    sections () {
      return uniq(this.options.reduce((acc, obj) => {
        if (obj.section) {
          return acc.concat([obj.section])
        }

        return acc
      }, [])).concat([ null ])
    }
  },

  methods: {
    includes: includes,
    onInput (opt) {
      let newValue

      if (includes(this.value, opt.value)) {
        newValue = this.value
          .filter(v => v !== opt.value)
      } else {
        newValue = this.value
          .concat([ opt.value ])
      }

      this.$emit('input', newValue)
    },
    optionsForSection (section) {
      if (isNull(section)) {
        return this.options
          .filter(opt => isUndefined(opt.section))
      }

      return this.options
        .filter(opt => opt.section === section)
    }
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebCheckboxGroup {
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;

  &-section + &-section {
    margin-top: 20px;
  }

  &-sectionTitle {
    h4 {
      font-family: $defaultFont;
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      color: $slate-grey;
      margin: 0;
      padding-bottom: 15px;
    }
  }

  &-checkboxes {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;

    &-checkbox {
      box-sizing: border-box;
      overflow: hidden;
      padding: 7.5px 15px;

      &.one {
        width: 100%;
      }

      &.two {
        width: calc(100% / 2);
      }

      &.three {
        width: calc(100% / 3);
      }

      &.four {
        width: calc(100% / 4);
      }

      &.five {
        width: calc(100% / 5);
      }
    }
  }
}
</style>
